import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { obterItem, salvarItem } from '../utils/localStorage';

type Tema = 'claro' | 'escuro';

interface ThemeContextType {
  tema: Tema;
  alternarTema: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [tema, setTema] = useState<Tema>(() => {
    try {
      const temaSalvo = obterItem<Tema | null>('TugasCoins-tema', null);
      // Se não houver tema salvo, ou se o tema salvo for inválido, use 'escuro' como padrão
      // dado o background escuro no README.
      return temaSalvo === 'claro' || temaSalvo === 'escuro' ? temaSalvo : 'escuro';
    } catch (error) {
      // Em caso de erro ao acessar localStorage (ex: segurança), defina um tema padrão
      console.error("Erro ao carregar tema do localStorage:", error);
      return 'escuro';
    }
  });

  const aplicarTemaAoDocumento = useCallback((temaAtual: Tema) => {
    const root = document.documentElement;
    if (temaAtual === 'escuro') {
      root.classList.add('dark');
      root.style.setProperty('--cor-primaria', '#d9a526');
      root.style.setProperty('--cor-secundaria', '#e8c97d');
      root.style.setProperty('--cor-danger', '#EF4444');
      root.style.setProperty('--cor-accent', '#c38d09');
      root.style.setProperty('--cor-background', '#121212');
      root.style.setProperty('--cor-texto', '#f2eee3');
      root.style.setProperty('--cor-warning', '#F59E0B');
      root.style.setProperty('--cor-success', '#10B981');
    } else {
      root.classList.remove('dark');
      // Definir cores para o tema claro (invertendo ou usando uma paleta clara)
      // Para um design moderno, vamos usar tons mais claros das cores existentes ou complementares.
      root.style.setProperty('--cor-primaria', '#c38d09'); // Um pouco mais escuro para contraste
      root.style.setProperty('--cor-secundaria', '#f2eee3'); // Quase branco
      root.style.setProperty('--cor-danger', '#DC2626'); // Vermelho mais vibrante
      root.style.setProperty('--cor-accent', '#d9a526'); // Primária como accent
      root.style.setProperty('--cor-background', '#FFFFFF'); // Branco puro
      root.style.setProperty('--cor-texto', '#121212'); // Background escuro como texto
      root.style.setProperty('--cor-warning', '#D97706'); // Laranja mais escuro
      root.style.setProperty('--cor-success', '#059669'); // Verde mais escuro
    }
    // Adiciona uma transição suave para as cores, melhorando a UX na troca de tema
    root.style.transition = 'background-color 0.3s ease, color 0.3s ease';
  }, []);

  useEffect(() => {
    aplicarTemaAoDocumento(tema);
    salvarItem('TugasCoins-tema', tema);
  }, [tema, aplicarTemaAoDocumento]);

  const alternarTema = useCallback(() => {
    setTema((temaAnterior) => (temaAnterior === 'escuro' ? 'claro' : 'escuro'));
  }, []);

  const valorContexto = React.useMemo(() => ({
    tema,
    alternarTema,
  }), [tema, alternarTema]);

  return (
    <ThemeContext.Provider value={valorContexto}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const contexto = useContext(ThemeContext);
  if (contexto === undefined) {
    throw new Error('useTheme deve ser utilizado dentro de um ThemeProvider');
  }
  return contexto;
};